/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import React, { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AddLangToPath } from "@/helper/AddLanguagePath";
import { getDashboardUrl, getAccountUrl, getAuthLoginUrl, isDeskshare } from "@/utils/siteConfig";
import { safeGetLang } from "@/helper/safeLocalStorage";
import type { NavItem, ProductData } from "./types";
import { EXCLUDED_PRODUCTS } from "./constants";

interface MobileNavbarProps {
  productData: ProductData | null;
  navItems: NavItem[];
  mobileNavbarItems: NavItem[];
  isLoggedIn: boolean;
  onClose: () => void;
}

const DropdownToggle: React.FC<{
  label: string;
  isOpen: boolean;
  onToggle: () => void;
}> = ({ label, isOpen, onToggle }) => (
  <button
    type="button"
    className="mobile-navbar__title mobile-navbar__dropdown-toggle"
    onClick={onToggle}
    aria-expanded={isOpen}
  >
    {label}
    {/* aria-hidden already set — the ▼ character is already excluded from AT.
        Verified: aria-hidden on the span correctly suppresses the glyph. */}
    <span
      className="mobile-navbar__chevron"
      data-open={isOpen ? "true" : "false"}
      aria-hidden="true"
    >
      ▼
    </span>
  </button>
);

const MobileNavbar: React.FC<MobileNavbarProps> = ({
  productData,
  navItems,
  mobileNavbarItems,
  isLoggedIn,
  onClose,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);

  const handleNavigation = useCallback(
    (link: string) => {
      navigate(link);
      onClose();
    },
    [navigate, onClose],
  );

  const toggleDropdown = useCallback((title: string) => {
    setOpenDropdown((prev) => (prev === title ? null : title));
  }, []);

  const renderNavButton = (label: string, link: string, forceHardNav?: boolean) => {
    // forceHardNav: server-routed paths (e.g., Traefik-proxied NodeBB forum) require
    // a full HTTP request — React Router navigate() bypasses Traefik entirely.
    if (forceHardNav) {
      return (
        <a
          key={link}
          className="mobile-navbar__item"
          href={link}
          onClick={onClose}
        >
          <span className="mobile-navbar__link">{label}</span>
        </a>
      );
    }
    // DD-1: React Router navigate() cannot handle absolute external URLs.
    // Detect external links and render <a target="_blank"> instead.
    const isExternal =
      link.startsWith("http://") || link.startsWith("https://");
    if (isExternal) {
      return (
        <a
          key={link}
          className="mobile-navbar__item"
          href={link}
          target="_blank"
          rel="noopener noreferrer"
          onClick={onClose}
        >
          <span className="mobile-navbar__link">{label}</span>
        </a>
      );
    }
    return (
      <button
        type="button"
        className="mobile-navbar__item"
        onClick={() => handleNavigation(link)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleNavigation(link);
          }
        }}
      >
        <span className="mobile-navbar__link">{label}</span>
      </button>
    );
  };

  const productTitle = t("header.product");

  return (
    <div className="mobile-navbar">
      <nav className="mobile-navbar__nav">
        <div className="mobile-navbar__section">
          <DropdownToggle
            label={productTitle}
            isOpen={openDropdown === productTitle}
            onToggle={() => toggleDropdown(productTitle)}
          />
          {openDropdown === productTitle && productData && (
            <div>
              {Object.entries(productData).map(([category, products]) => {
                const activeProducts = products.filter(
                  (product) =>
                    !EXCLUDED_PRODUCTS.includes(product.product_name),
                );

                if (activeProducts.length === 0) return null;

                return (
                  <React.Fragment key={category}>
                    <div className="mobile-navbar__category-header">
                      {category}
                    </div>
                    {activeProducts.map((product) => (
                      <React.Fragment key={product.product_slug}>
                        {renderNavButton(
                          product.product_name,
                          AddLangToPath(`/${product.product_slug}`),
                        )}
                        <hr className="mobile-navbar__divider" />
                      </React.Fragment>
                    ))}
                  </React.Fragment>
                );
              })}
            </div>
          )}
        </div>

        {navItems.map((section) => (
          <div key={section.title} className="mobile-navbar__section">
            <DropdownToggle
              label={section.title}
              isOpen={openDropdown === section.title}
              onToggle={() => toggleDropdown(section.title)}
            />
            {openDropdown === section.title && (
              <div>
                {mobileNavbarItems
                  .find((item) => item.title === section.title)
                  ?.items.map((item) => (
                    <React.Fragment key={item.link}>
                      {renderNavButton(item.title, item.link, item.forceHardNav)}
                      <hr className="mobile-navbar__divider" />
                    </React.Fragment>
                  ))}
              </div>
            )}
          </div>
        ))}
      </nav>
      <button
        type="button"
        className="mobile-navbar__signin-button"
        onClick={() => {
          const lang = safeGetLang() ?? "en";
          window.location.href = isLoggedIn
            ? isDeskshare()
              ? getAccountUrl(lang)
              : getDashboardUrl(lang)
            : isDeskshare()
              ? getAuthLoginUrl()
              : getDashboardUrl(lang);
        }}
      >
        {isLoggedIn ? t("header.my_account") : t("header.sign_in")}
      </button>
    </div>
  );
};

export default MobileNavbar;
